import Vuex from "vuex";
import Vue from "vue";

import getApartment from "../../lib/getApartment";
import localSave from "./localSave";

Vue.use(Vuex);

function getFavourites(type) {
  return JSON.parse(localStorage.getItem(`favourites-${type}`)) || [];
}

function storeFavourites(type, favourites) {
  localStorage.setItem(`favourites-${type}`, JSON.stringify(favourites));
}

export default new Vuex.Store({
  modules: {
    localSave,
  },
  state: {
    // Favourites as UUIDs. Loaded from localstorage on every page load. The source of truth.
    favourites: {
      "right-of-occupancy": getFavourites("right-of-occupancy"),
      rental: getFavourites("rental"),
      "owner-occupancy": getFavourites("owner-occupancy"),
    },
    // Favourites as apartment objects. Populated using requestAndCleanUpFavourites whenever needed.
    favouritesData: {
      "right-of-occupancy": [],
      "owner-occupancy": [],
      rental: [],
    },
    favouritesDataLoading: false,
    favouritesMenuOpenExternal: false,
  },
  getters: {
    favouritesAll: (state) => {
      return Object.values(state.favourites).flat();
    },
    favouritesRental: (state) => {
      return Object.values(state.favourites.rental).flat();
    },
    favouritesASO: (state) => {
      return Object.values(state.favourites["right-of-occupancy"]).flat();
    },
    favouritesDataAll: (state) => {
      return Object.values(state.favouritesData).flat();
    },
    isFavourite: (state, getters) => (uuid) => {
      return getters.favouritesAll.includes(uuid);
    },
    getFavouritesMenuOpenExternal: (state) => {
      return state.favouritesMenuOpenExternal;
    },
  },
  mutations: {
    emptyFavourites(state, args = {}) {
      console.log("empty favourites", state);
      const types = args.hasOwnProperty("type")
        ? [args.type]
        : Object.keys(state.favourites);
      types.forEach((type) => {
        state.favourites[type] = [];
        storeFavourites(type, state.favourites[type]);
      });
    },
    addFavourite(state, { type, uuid }) {
      if (state.favourites[type].includes(uuid)) {
        return;
      }

      state.favourites[type].push(uuid);

      storeFavourites(type, state.favourites[type]);
    },
    removeFavourite(state, { type, uuid }) {
      const index = state.favourites[type].indexOf(uuid);
      if (index === -1) {
        return;
      }

      state.favourites[type].splice(index, 1);

      storeFavourites(type, state.favourites[type]);
    },
    updateFavouritesData(state, { apartments }) {
      Object.keys(state.favouritesData).forEach((type) => {
        state.favouritesData[type] = state.favourites[type]
          .map((uuid) =>
            apartments.find((apartment) => apartment.mpc_asuntoid === uuid)
          )
          // Sort according to lexical order - cities first, then street name.
          .sort((apartment1, apartment2) =>
            apartment1.mpc_name > apartment2.mpc_name ? 1 : -1
          )
          .sort((apartment1, apartment2) => {
            if (apartment1.city.mpc_name === apartment2.city.mpc_name) return 0;
            return apartment1.city.mpc_name > apartment2.city.mpc_name ? 1 : -1;
          })
          .filter((apartment) => apartment);
      });
    },
    increaseFavouritesDataLoading(state) {
      state.favouritesDataLoading++;
    },
    decreaseFavouritesDataLoading(state) {
      state.favouritesDataLoading--;
    },
    toggleFavouritesMenuExternal(state, value) {
      state.favouritesMenuOpenExternal = value;
    },
  },
  actions: {
    // Favourite apartments that are no longer publicly accessible must be removed from favourites when encountered.
    async requestAndCleanUpFavourites({ state, commit }) {
      // A copy of favourites is created so that future changes won't mess with clean up logic later.
      const favouritesCopy = {
        "right-of-occupancy": [...state.favourites["right-of-occupancy"]],
        "owner-occupancy": [...state.favourites["owner-occupancy"]],
        rental: [...state.favourites["rental"]],
      };

      const favourites = [
        ...favouritesCopy["right-of-occupancy"],
        ...favouritesCopy["owner-occupancy"],
        ...favouritesCopy["rental"],
      ];

      commit("increaseFavouritesDataLoading");

      const apartments = await getApartment(favourites);

      // Clean up all missing apartments by comparing requested UUIDs (favourites) to resulted apartments.
      const cleanUpFavourites = (type) => {
        favouritesCopy[type].forEach((uuid) => {
          const found = apartments.find(
            (apartment) => apartment.mpc_asuntoid === uuid
          );
          if (found) {
            return;
          }
          commit("removeFavourite", { uuid, type });
        });
      };

      cleanUpFavourites("right-of-occupancy");
      cleanUpFavourites("owner-occupancy");
      cleanUpFavourites("rental");

      commit("updateFavouritesData", { apartments });
      commit("decreaseFavouritesDataLoading");
    },
  },
});
